import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "../api/client";
import { useI18n } from "../i18n";
import { PageHeader, Card, Badge, EmptyState } from "../components/shared";
import {
  Plus, CalendarDays, X, ChevronLeft, ChevronRight, Clock, AlertTriangle,
  CheckCircle2, UserCheck, Stethoscope, XCircle, Hourglass, PlayCircle, Archive,
} from "lucide-react";
import { toast } from "sonner";
import BulkArchiveDialog, { ArchiveRowButton, ArchiveTabs } from "../components/Archive";

function todayStr() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

// Status visual config
const STATUS_CFG = {
  scheduled:       { tone: "info",     bg: "bg-[#2563eb]/10",  fg: "text-[#1e40af]",  border: "border-[#2563eb]/30" },
  confirmed:       { tone: "info",     bg: "bg-[#2563eb]/10",  fg: "text-[#1e40af]",  border: "border-[#2563eb]/30" },
  arrived:         { tone: "warning",  bg: "bg-[#e3a03b]/15",  fg: "text-[#8a5a10]",  border: "border-[#e3a03b]/40" },
  in_consultation: { tone: "neutral",  bg: "bg-[#7c3aed]/10",  fg: "text-[#5b21b6]",  border: "border-[#7c3aed]/30" },
  completed:       { tone: "success",  bg: "bg-[#2d8a56]/10",  fg: "text-[#2d8a56]",  border: "border-[#2d8a56]/30" },
  cancelled:       { tone: "danger",   bg: "bg-[#d34044]/10",  fg: "text-[#b12e31]",  border: "border-[#d34044]/30" },
  no_show:         { tone: "danger",   bg: "bg-[#d34044]/10",  fg: "text-[#b12e31]",  border: "border-[#d34044]/30" },
};

function StatusBadge({ status, t }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.scheduled;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.fg}`}>
      {t(`appointments.status.${status}`)}
    </span>
  );
}

function MetricCard({ icon: Icon, label, value, tone = "brand", testid }) {
  const tones = {
    brand: "bg-brand/10 text-brand",
    warning: "bg-[#e3a03b]/15 text-[#8a5a10]",
    success: "bg-[#2d8a56]/10 text-[#2d8a56]",
    danger: "bg-[#d34044]/10 text-[#b12e31]",
    purple: "bg-[#7c3aed]/10 text-[#5b21b6]",
  };
  return (
    <Card className="p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${tones[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-ink-muted font-medium uppercase tracking-wider truncate">{label}</div>
          <div className="stat-number text-2xl font-bold text-ink leading-tight">{value}</div>
        </div>
      </div>
    </Card>
  );
}

function minutesBetween(a, b) {
  return Math.floor((new Date(b).getTime() - new Date(a).getTime()) / 60000);
}

export default function Appointments() {
  const { t, lang } = useI18n();
  const [day, setDay] = useState(todayStr());
  const [items, setItems] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [now, setNow] = useState(() => new Date());
  const [archiveView, setArchiveView] = useState("active");
  const [archivedCount, setArchivedCount] = useState(0);
  const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false);

  function emptyForm() {
    return { patient_id: "", doctor_id: "", start_at: "", end_at: "", reason: "" };
  }

  // Re-render every minute so wait/late timers update live
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const load = async () => {
    const from = `${day}T00:00:00`;
    const to = `${day}T23:59:59+00:00`;
    const params = { from, to };
    if (archiveView === "archived") params.archived = "true";
    const [list, archCount] = await Promise.all([
      api.get("/appointments", { params }),
      api.get("/appointments", { params: { archived: "true" } }),
    ]);
    setItems(list.data);
    setArchivedCount(archCount.data.length);
  };

  useEffect(() => {
    (async () => {
      const [d, p] = await Promise.all([
        api.get("/users", { params: { role: "doctor" } }),
        api.get("/patients", { params: { limit: 500 } }),
      ]);
      setDoctors(d.data);
      setPatients(p.data);
    })();
  }, []);

  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day, archiveView]);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const pid = searchParams.get("patient_id");
    if (pid) {
      setForm((f) => ({ ...f, patient_id: pid }));
      setOpenNew(true);
      const next = new URLSearchParams(searchParams);
      next.delete("patient_id");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/appointments", {
        ...form,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at || form.start_at).toISOString(),
      });
      toast.success(t("common.success"));
      setForm(emptyForm());
      setOpenNew(false);
      load();
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.post(`/appointments/${id}/status`, { status });
      toast.success(t("common.success"));
      load();
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    }
  };

  const shift = (delta) => {
    const d = new Date(day + "T12:00:00");
    d.setDate(d.getDate() + delta);
    setDay(d.toISOString().slice(0, 10));
  };

  // ----- Computed sets -----
  const metrics = useMemo(() => {
    const m = { total: items.length, completed: 0, waiting: 0, in_progress: 0, no_show: 0, scheduled: 0 };
    items.forEach((a) => {
      if (a.status === "completed") m.completed += 1;
      else if (a.status === "arrived") m.waiting += 1;
      else if (a.status === "in_consultation") m.in_progress += 1;
      else if (a.status === "no_show") m.no_show += 1;
      else if (a.status === "scheduled" || a.status === "confirmed") m.scheduled += 1;
    });
    return m;
  }, [items]);

  const waitingPatients = useMemo(() => {
    return items
      .filter((a) => a.status === "arrived")
      .map((a) => ({
        ...a,
        wait_minutes: a.arrived_at ? minutesBetween(a.arrived_at, now) : 0,
      }))
      .sort((a, b) => b.wait_minutes - a.wait_minutes);
  }, [items, now]);

  const grouped = useMemo(() => {
    const by = {};
    items.forEach((a) => {
      const key = a.doctor?.id || a.doctor_id;
      if (!by[key]) by[key] = { doctor: a.doctor || { id: key, first_name: "?", last_name: "" }, items: [] };
      by[key].items.push(a);
    });
    return Object.values(by);
  }, [items]);

  const input = "w-full rounded-xl border border-[var(--border-default)] bg-white px-4 h-12 text-base focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none";

  return (
    <div>
      <PageHeader
        title={t("appointments.title")}
        subtitle={new Date(day + "T12:00:00").toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <ArchiveTabs value={archiveView} onChange={setArchiveView} activeCount={archiveView === "active" ? items.length : null} archivedCount={archivedCount} />
            {archiveView === "active" && (
              <>
                <button onClick={() => setBulkArchiveOpen(true)} data-testid="bulk-archive-btn"
                  className="h-11 px-4 rounded-xl border border-[var(--border-default)] bg-white hover:bg-subtle font-semibold text-sm flex items-center gap-2">
                  <Archive className="w-4 h-4" /> Archiver…
                </button>
                <button onClick={() => setOpenNew(true)} data-testid="new-appointment-btn"
                  className="h-12 px-5 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold flex items-center gap-2">
                  <Plus className="w-5 h-5" /> {t("appointments.new")}
                </button>
              </>
            )}
          </div>
        }
      />

      {/* Date navigation */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <button onClick={() => shift(-1)} data-testid="day-prev" className="h-11 w-11 rounded-xl bg-white border border-[var(--border-default)] flex items-center justify-center hover:bg-subtle">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button onClick={() => setDay(todayStr())} data-testid="day-today" className="h-11 px-4 rounded-xl bg-white border border-[var(--border-default)] font-semibold hover:bg-subtle">
          {t("common.today")}
        </button>
        <input type="date" value={day} onChange={(e) => setDay(e.target.value)} data-testid="day-picker"
          className="h-11 rounded-xl border border-[var(--border-default)] bg-white px-3 font-semibold" />
        <button onClick={() => shift(1)} data-testid="day-next" className="h-11 w-11 rounded-xl bg-white border border-[var(--border-default)] flex items-center justify-center hover:bg-subtle">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Daily metrics */}
      <section className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <MetricCard testid="m-total" icon={CalendarDays} label={t("appointments.metricsTotal")} value={metrics.total} tone="brand" />
        <MetricCard testid="m-waiting" icon={Hourglass} label={t("appointments.metricsWaiting")} value={metrics.waiting} tone="warning" />
        <MetricCard testid="m-in-progress" icon={PlayCircle} label={t("appointments.metricsInProgress")} value={metrics.in_progress} tone="purple" />
        <MetricCard testid="m-completed" icon={CheckCircle2} label={t("appointments.metricsCompleted")} value={metrics.completed} tone="success" />
        <MetricCard testid="m-noshow" icon={XCircle} label={t("appointments.metricsNoShow")} value={metrics.no_show} tone="danger" />
      </section>

      {/* WAITING PATIENTS */}
      <Card className="mb-6 p-5 border-[#e3a03b]/30 bg-gradient-to-r from-[#e3a03b]/10 to-transparent" data-testid="waiting-section">
        <div className="flex items-center gap-2 mb-4">
          <Hourglass className="w-5 h-5 text-[#8a5a10]" />
          <h3 className="font-heading font-bold text-lg text-ink">{t("appointments.waiting")}</h3>
          <Badge tone="warning">{waitingPatients.length}</Badge>
        </div>
        {waitingPatients.length === 0 ? (
          <div className="text-sm text-ink-muted py-2">{t("appointments.noWaiting")}</div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {waitingPatients.map((a) => {
              const overWait = a.wait_minutes >= 15;
              return (
                <li key={a.id} data-testid={`waiting-${a.id}`}
                  className={`p-4 rounded-xl bg-white border ${overWait ? "border-[#d34044]/40 ring-1 ring-[#d34044]/20" : "border-[#e3a03b]/30"} flex items-center gap-3`}>
                  <div className={`w-10 h-10 rounded-full ${overWait ? "bg-[#d34044]/15 text-[#b12e31]" : "bg-[#e3a03b]/15 text-[#8a5a10]"} flex items-center justify-center font-heading font-bold`}>
                    {a.queue_position || "•"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-ink truncate">
                      {a.patient ? `${a.patient.first_name} ${a.patient.last_name}` : "—"}
                    </div>
                    <div className={`text-xs font-semibold flex items-center gap-1 mt-0.5 ${overWait ? "text-[#b12e31]" : "text-[#8a5a10]"}`}>
                      <Clock className="w-3 h-3" />
                      {t("appointments.waitingTime")} {a.wait_minutes} min
                      {overWait && <AlertTriangle className="w-3 h-3 ml-1" />}
                    </div>
                  </div>
                  <button
                    onClick={() => changeStatus(a.id, "in_consultation")}
                    data-testid={`waiting-start-${a.id}`}
                    className="px-3 py-2 rounded-lg bg-brand hover:bg-brand-hover text-white text-xs font-bold flex items-center gap-1 whitespace-nowrap"
                  >
                    <Stethoscope className="w-3.5 h-3.5" /> {t("appointments.startConsult")}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* PER-DOCTOR BOARDS */}
      {items.length === 0 ? (
        <Card className="p-6"><EmptyState icon={CalendarDays} title={t("common.noData")} /></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {grouped.map((g) => (
            <Card key={g.doctor.id} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-heading font-bold text-ink">
                    Dr. {g.doctor.first_name} {g.doctor.last_name}
                  </div>
                  {g.doctor.specialty && <div className="text-xs text-ink-muted">{t(`specialties.${g.doctor.specialty}`)}</div>}
                </div>
                <Badge tone="brand">{g.items.length}</Badge>
              </div>
              <ul className="space-y-2">
                {g.items.sort((a, b) => a.start_at.localeCompare(b.start_at)).map((a) => {
                  const cfg = STATUS_CFG[a.status] || STATUS_CFG.scheduled;
                  // Delay: minutes the patient is late vs scheduled start (if not arrived/completed yet)
                  const minutesLate = (() => {
                    if (a.status === "scheduled" || a.status === "confirmed") {
                      const delta = minutesBetween(a.start_at, now);
                      return delta > 0 ? delta : 0;
                    }
                    return 0;
                  })();
                  // Wait time once arrived
                  const waitMin = a.status === "arrived" && a.arrived_at ? minutesBetween(a.arrived_at, now) : 0;

                  return (
                    <li key={a.id} data-testid={`apt-${a.id}`}
                      className={`p-4 rounded-xl border ${cfg.border} ${cfg.bg}`}>
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="text-center shrink-0">
                            <div className="stat-number font-bold text-ink text-lg leading-none">
                              {new Date(a.start_at).toLocaleTimeString(lang === "fr" ? "fr-FR" : "en-GB", { hour: "2-digit", minute: "2-digit" })}
                            </div>
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-ink truncate">
                              {a.patient ? `${a.patient.first_name} ${a.patient.last_name}` : a.patient_id}
                            </div>
                            {a.reason && <div className="text-xs text-ink-muted truncate">{a.reason}</div>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusBadge status={a.status} t={t} />
                          {(["completed", "cancelled", "no_show"].includes(a.status) || archiveView === "archived") && (
                            <ArchiveRowButton kind="appointments" itemId={a.id} archived={archiveView === "archived"} onDone={load} />
                          )}
                        </div>
                      </div>

                      {/* Inline indicators */}
                      {(minutesLate >= 5 || waitMin > 0) && (
                        <div className="mb-2 flex flex-wrap gap-2">
                          {minutesLate >= 5 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#d34044]/10 text-[#b12e31] text-xs font-bold">
                              <AlertTriangle className="w-3 h-3" /> {t("appointments.delayLate")} +{minutesLate} min
                            </span>
                          )}
                          {waitMin > 0 && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold
                              ${waitMin >= 15 ? "bg-[#d34044]/10 text-[#b12e31]" : "bg-[#e3a03b]/15 text-[#8a5a10]"}`}>
                              <Clock className="w-3 h-3" /> {t("appointments.waitingTime")} {waitMin} min
                            </span>
                          )}
                        </div>
                      )}

                      {/* CONTEXTUAL ACTIONS */}
                      <div className="flex flex-wrap gap-2">
                        {(a.status === "scheduled" || a.status === "confirmed") && (
                          <>
                            <button onClick={() => changeStatus(a.id, "arrived")} data-testid={`apt-arrived-${a.id}`}
                              className="px-3 py-1.5 rounded-lg bg-[#e3a03b]/15 text-[#8a5a10] text-xs font-bold hover:bg-[#e3a03b]/25 flex items-center gap-1">
                              <UserCheck className="w-3.5 h-3.5" /> {t("appointments.markArrived")}
                            </button>
                            <button onClick={() => changeStatus(a.id, "no_show")} data-testid={`apt-noshow-${a.id}`}
                              className="px-3 py-1.5 rounded-lg bg-[#d34044]/10 text-[#b12e31] text-xs font-bold hover:bg-[#d34044]/20 flex items-center gap-1">
                              <XCircle className="w-3.5 h-3.5" /> {t("appointments.noShow")}
                            </button>
                            <button onClick={() => changeStatus(a.id, "cancelled")} data-testid={`apt-cancel-${a.id}`}
                              className="px-3 py-1.5 rounded-lg bg-subtle text-ink-muted text-xs font-semibold hover:bg-[#d34044]/10 hover:text-[#b12e31] flex items-center gap-1">
                              {t("appointments.cancel")}
                            </button>
                          </>
                        )}
                        {a.status === "arrived" && (
                          <button onClick={() => changeStatus(a.id, "in_consultation")} data-testid={`apt-start-${a.id}`}
                            className="px-3 py-1.5 rounded-lg bg-brand text-white text-xs font-bold hover:bg-brand-hover flex items-center gap-1">
                            <Stethoscope className="w-3.5 h-3.5" /> {t("appointments.startConsult")}
                          </button>
                        )}
                        {a.status === "in_consultation" && (
                          <button onClick={() => changeStatus(a.id, "completed")} data-testid={`apt-complete-${a.id}`}
                            className="px-3 py-1.5 rounded-lg bg-[#2d8a56] text-white text-xs font-bold hover:bg-[#236d44] flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" /> {t("appointments.complete")}
                          </button>
                        )}
                        {/* Completed / cancelled / no_show: no actions */}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Card>
          ))}
        </div>
      )}

      {/* New appointment modal */}
      {openNew && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto animate-fade-in">
            <div className="flex items-center justify-between p-6 border-b border-[var(--border-default)]">
              <h3 className="font-heading font-bold text-xl">{t("appointments.new")}</h3>
              <button onClick={() => setOpenNew(false)} className="p-2 rounded-lg hover:bg-subtle"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <label className="block mb-2 text-sm font-semibold">{t("appointments.patient")}</label>
                <select required className={input} value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })} data-testid="apt-patient">
                  <option value="">—</option>
                  {patients.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block mb-2 text-sm font-semibold">{t("appointments.doctor")}</label>
                <select required className={input} value={form.doctor_id} onChange={(e) => setForm({ ...form, doctor_id: e.target.value })} data-testid="apt-doctor">
                  <option value="">—</option>
                  {doctors.map((d) => <option key={d.id} value={d.id}>Dr. {d.first_name} {d.last_name}{d.specialty ? ` · ${t(`specialties.${d.specialty}`)}` : ""}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block mb-2 text-sm font-semibold">{t("appointments.start")}</label>
                  <input type="datetime-local" required className={input} value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} data-testid="apt-start" />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-semibold">{t("appointments.end")}</label>
                  <input type="datetime-local" required className={input} value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} data-testid="apt-end" />
                </div>
              </div>
              <div>
                <label className="block mb-2 text-sm font-semibold">{t("appointments.reason")}</label>
                <input className={input} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="apt-reason" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setOpenNew(false)} className="flex-1 h-12 rounded-xl border border-[var(--border-default)] font-semibold">{t("common.cancel")}</button>
                <button type="submit" data-testid="apt-save" className="flex-1 h-12 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold">{t("common.save")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {bulkArchiveOpen && (
        <BulkArchiveDialog kind="appointments" doctors={doctors}
          onClose={() => setBulkArchiveOpen(false)}
          onDone={() => { setBulkArchiveOpen(false); load(); }} />
      )}
    </div>
  );
}
